import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar } from "lucide-react";

/**
 * Parse yyyy-mm-dd (or yyyy-mm) as a local calendar date.
 * `new Date("2026-03-01")` is interpreted as UTC midnight and shifts the calendar month in some timezones.
 */
function parseLocalDateFromValue(value) {
  if (!value) return null;
  const s = String(value).split("/")[0].trim();
  const m = s.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const day = m[3] != null ? Number(m[3]) : 1;
  if (!y || mo < 1 || mo > 12 || day < 1 || day > 31) return null;
  const d = new Date(y, mo - 1, day);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Reusable lightweight DatePicker
 * - Views: day, month (grid), year (3x4 grid)
 * - Header nav: prev/next, month button, year button (consistent styles)
 * - Year grid: 3 columns x 4 rows with horizontal paging of 12-year blocks
 * - Props mimic native input usage where possible
 *   value: yyyy-mm-dd
 *   onChange: (eventLike: { target: { value: yyyy-mm-dd } })
 *   min?: yyyy-mm-dd
 *   max?: yyyy-mm-dd
 *   disabled?: boolean
 *   darkMode?: boolean
 *   label?: string (optional, shown inside the trigger)
 *   className?: string (applied to trigger button)
 */
export default function DatePicker({
  value,
  onChange,
  min,
  max,
  disabled = false,
  darkMode = false,
  label,
  className = "",
  error,
  minErrorText,
  maxErrorText,
  validateOnType = false,
  required = false,
  monthYearOnly = false,
  showMonthName = false,       // if true, display month name for monthYearOnly
  monthLabelPrefix = "",
  /** When monthYearOnly and value is empty, show this instead of a date label */
  monthYearEmptyLabel = "",
  dropdownAlign = "left",      // "left" | "right" | "auto" - where dropdown aligns; auto = pick side with more space
}) {
  const parsed = useMemo(() => {
    if (!value) return new Date();
    const local = parseLocalDateFromValue(value);
    if (local) return local;
    return new Date(String(value).split("/")[0] ?? value);
  }, [value]);
  const [open, setOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState("left");
  const [view, setView] = useState(monthYearOnly ? "month" : "day");
  const [viewYear, setViewYear] = useState(parsed.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed.getMonth());
  const [inputText, setInputText] = useState("");
  const [typingError, setTypingError] = useState("");
  const [touched, setTouched] = useState(false);
  const MIN_YEAR = 1884;
  const MAX_YEAR = 2150;

  const ref = useRef(null);
  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Auto-position dropdown: prefer anchoring so the panel stays in the viewport (w-80 ≈ 320px).
  useEffect(() => {
    if (!open || dropdownAlign !== "auto" || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    const panelW = 320;
    const margin = 12;
    const spaceRight = vw - rect.right - margin;
    const spaceLeft = rect.left - margin;
    // If opening to the right of the trigger's left edge would overflow, anchor to the right instead.
    if (rect.left + panelW > vw - margin && spaceRight < panelW && spaceLeft >= spaceRight) {
      setDropdownPosition("right");
    } else if (spaceRight < panelW && spaceLeft > spaceRight) {
      setDropdownPosition("right");
    } else {
      setDropdownPosition("left");
    }
  }, [open, dropdownAlign]);

  // Run required validation when touched or when value prop changes
  useEffect(() => {
    if (required) {
      const hasValue = Boolean(value && String(value).trim() !== "");
      if (touched && !hasValue) {
        setTypingError("This field is required");
      } else if (hasValue) {
        setTypingError("");
      }
    }
  }, [required, touched, value]);

  const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const minDate = min ? new Date(min) : null;
  const maxDate = max ? new Date(max) : null;
  const allowedMin = stripTime(minDate ?? new Date(MIN_YEAR, 0, 1));
  const allowedMax = stripTime(maxDate ?? new Date(MAX_YEAR, 11, 31));
  const allowedMaxDayKey = allowedMax.getTime();

  // Sync calendar view when value changes (after allowedMin/allowedMax exist)
  useEffect(() => {
    if (value && String(value).trim() !== "") {
      const raw = String(value).split("/")[0] ?? value;
      const d = parseLocalDateFromValue(raw) || new Date(raw);
      if (!isNaN(d)) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    } else if (monthYearOnly) {
      const d = stripTime(allowedMax);
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
      setInputText("");
    }
  }, [value, monthYearOnly, allowedMaxDayKey]);

  useEffect(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const last = new Date(viewYear, viewMonth + 1, 0);
    if (last < allowedMin) {
      setViewYear(allowedMin.getFullYear());
      setViewMonth(allowedMin.getMonth());
    } else if (first > allowedMax) {
      setViewYear(allowedMax.getFullYear());
      setViewMonth(allowedMax.getMonth());
    }
  }, [open, allowedMin, allowedMax]);

  const toDisplay = (d) => {
    const day = String(d.getDate()).padStart(2, "0");
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const y = d.getFullYear();
    return `${day}-${m}-${y}`;
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const formatMonthLabel = (monthIndex, year) => {
    if (!showMonthName) return `${String(monthIndex + 1).padStart(2, "0")}-${year}`;
    // Example outputs:
    // when monthLabelPrefix === "" => "November 2025"
    // when monthLabelPrefix === "Month " => "Month November 2025"
    return `${monthLabelPrefix}${months[monthIndex]} - ${year}`;
  };

  // Initialize input fields when value changes
  useEffect(() => {
    if (value) {
      const parts = String(value).split("/");
      const raw = parts[0];
      const d = parseLocalDateFromValue(raw) || new Date(raw);
      if (!isNaN(d)) {
        if (dayRef.current) dayRef.current.value = String(d.getDate()).padStart(2, "0");
        if (monthRef.current) monthRef.current.value = String(d.getMonth() + 1).padStart(2, "0");
        if (yearRef.current) yearRef.current.value = String(d.getFullYear());
        setInputText(monthYearOnly ? formatMonthLabel(d.getMonth(), d.getFullYear()) : toDisplay(d));
      }
    } else {
      if (dayRef.current) dayRef.current.value = "";
      if (monthRef.current) monthRef.current.value = "";
      if (yearRef.current) yearRef.current.value = "";
      setInputText("");
    }
    setTypingError("");
  }, [value, monthYearOnly]);

  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstDayIndex = (y, m) => new Date(y, m, 1).getDay();

  const isBeforeMin = (d) => d < allowedMin;
  const isAfterMax = (d) => d > allowedMax;

  const selectDay = (d) => {
    if (disabled || monthYearOnly) return;
    const date = new Date(viewYear, viewMonth, d);
    const s = stripTime(date);
    if (isBeforeMin(s) || isAfterMax(s)) return;
    
    // FIXED: Update all input fields when selecting from calendar
    if (dayRef.current) dayRef.current.value = String(d).padStart(2, "0");
    if (monthRef.current) monthRef.current.value = String(viewMonth + 1).padStart(2, "0");
    if (yearRef.current) yearRef.current.value = String(viewYear);
    
    setInputText(toDisplay(date));
    onChange?.({ target: { value: toISO(date) } });
    setOpen(false);
    // mark as interacted
    setTouched(true);
    if (required) setTypingError("");
  };

  // Select a specific Date object (used for adjacent month cells)
  const selectDate = (date) => {
    if (disabled || monthYearOnly) return;
    const s = stripTime(date);
    if (isBeforeMin(s) || isAfterMax(s)) return;
    
    // FIXED: Update all input fields when selecting from calendar
    if (dayRef.current) dayRef.current.value = String(date.getDate()).padStart(2, "0");
    if (monthRef.current) monthRef.current.value = String(date.getMonth() + 1).padStart(2, "0");
    if (yearRef.current) yearRef.current.value = String(date.getFullYear());
    
    setInputText(toDisplay(date));
    onChange?.({ target: { value: toISO(date) } });
    setViewYear(date.getFullYear());
    setViewMonth(date.getMonth());
    setOpen(false);
    setTouched(true);
    if (required) setTypingError("");
  };

  const parseDisplay = (text) => {
    const t = text.trim();
    let m = t.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})$/);
    if (!m) return null;
    const dd = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    const yy = parseInt(m[3], 10);
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
    const d = new Date(yy, mm - 1, dd);
    if (isNaN(d)) return null;
    if (d.getFullYear() !== yy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
    return d;
  };

  const validateDatePart = (day, month, year) => {
    if (!day && !month && !year) {
      setTypingError("");
      return true;
    }

    // Validate day range immediately
    if (day && (parseInt(day) < 1 || parseInt(day) > 31)) {
      setTypingError("Day must be between 01 and 31");
      return false;
    }

    // Validate month range immediately
    if (month && (parseInt(month) < 1 || parseInt(month) > 12)) {
      setTypingError("Month must be between 01 and 12");
      return false;
    }

    // Validate year range immediately when we have 4 digits
    if (year && year.length === 4) {
      if(year.length!==4){
        setTypingError("Year must be 4 digits");
        return false;
      }
      const yearNum = parseInt(year);
      if (yearNum < MIN_YEAR || yearNum > MAX_YEAR) {
        setTypingError(`Year must be between ${MIN_YEAR} and ${MAX_YEAR}`);
        return false;
      }
    }

    // VALIDATE FULL DATE AS SOON AS WE HAVE ALL PARTS
    if (day && month && year && day.length === 2 && month.length === 2 && year.length === 4) {
      const formatted = `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}`;
      const parsed = parseDisplay(formatted);
      
      if (!parsed) {
        setTypingError("Invalid date");
        return false;
      }

      const s = stripTime(parsed);
      if (isBeforeMin(s)) {
        setTypingError(minErrorText || `Date must be after ${toDisplay(allowedMin)}`);
        return false;
      } else if (isAfterMax(s)) {
        setTypingError(maxErrorText || `Date must be before ${toDisplay(allowedMax)}`);
        return false;
      }
    }

    setTypingError("");
    return true;
  };

  const handleKeyDown = (e, nextRef, prevRef) => {
    const { selectionStart, selectionEnd, value } = e.target;

    if (e.key === "ArrowRight" && selectionStart === value.length && selectionEnd === value.length) {
      e.preventDefault();
      nextRef?.current?.focus();
      nextRef?.current?.setSelectionRange(0, 0);
    } else if (e.key === "ArrowLeft" && selectionStart === 0 && selectionEnd === 0) {
      e.preventDefault();
      prevRef?.current?.focus();
      prevRef?.current?.setSelectionRange(prevRef.current.value.length, prevRef.current.value.length);
    } else if (e.key === "Backspace" && selectionStart === 0 && selectionEnd === 0 && prevRef) {
      e.preventDefault();
      prevRef.current.focus();
      prevRef.current.setSelectionRange(prevRef.current.value.length, prevRef.current.value.length);
    } else if (e.key === "Delete" && selectionStart === value.length && selectionEnd === value.length && nextRef) {
      e.preventDefault();
      nextRef.current.focus();
      nextRef.current.setSelectionRange(0, 0);
    }
  };

  const handlePaste = (e) => {
      if (monthYearOnly) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const digits = paste.replace(/\D/g, "").slice(0, 8);
    
    if (digits.length === 8) {
      const day = digits.slice(0, 2);
      const month = digits.slice(2, 4);
      const year = digits.slice(4, 8);
      
      if (dayRef.current) dayRef.current.value = day;
      if (monthRef.current) monthRef.current.value = month;
      if (yearRef.current) yearRef.current.value = year;
      
      handlePartChange('year', year);
      yearRef.current?.focus();
    }
  };

  // Fixed: Validate on every change and update calendar accordingly
  const handlePartChange = (field, value) => {
    // Remove non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    
    // Update the field value
    if (field === 'day' && dayRef.current) {
      dayRef.current.value = numericValue;
      // Auto-advance to month when 2 digits are entered
      if (numericValue.length === 2) {
        monthRef.current?.focus();
        monthRef.current?.select();
      }
    } 
    else if (field === 'month' && monthRef.current) {
      // Store raw value first
      monthRef.current.value = numericValue;
      
      // Smart auto-advance logic
      if (numericValue.length === 1) {
        const firstDigit = parseInt(numericValue);
        if (firstDigit >= 2 && firstDigit <= 9) {
          // Auto-advance to year for months 2-9, but ensure proper display
          setTimeout(() => {
            if (monthRef.current) {
              monthRef.current.value = String(firstDigit).padStart(2, '0');
            }
            yearRef.current?.focus();
            yearRef.current?.select();
          }, 10);
        }
      } else if (numericValue.length === 2) {
        // Two digits entered, move to year and ensure proper display
        setTimeout(() => {
          if (monthRef.current) {
            monthRef.current.value = numericValue.padStart(2, '0');
          }
          yearRef.current?.focus();
          yearRef.current?.select();
        }, 10);
      }
    }
    else if (field === 'year' && yearRef.current) {
      yearRef.current.value = numericValue;
    }

    // Get current values for validation
    const currentDay = field === 'day' ? numericValue : dayRef.current?.value || "";
    const currentMonth = field === 'month' ? numericValue : monthRef.current?.value || "";
    const currentYear = field === 'year' ? numericValue : yearRef.current?.value || "";

    // Update inputText properly
    const displayDay = currentDay.padStart(2, '0');
    const displayMonth = currentMonth.padStart(2, '0');
    const displayYear = currentYear;
    
    if (currentDay && currentMonth && currentYear) {
      setInputText(`${displayDay}-${displayMonth}-${displayYear}`);
    } else {
      setInputText("");
    }

    // VALIDATE ON EVERY CHANGE - not just when complete
    const isValid = validateDatePart(currentDay, currentMonth, currentYear);

    // UPDATE CALENDAR VIEW WHENEVER WE HAVE VALID MONTH AND YEAR
    if (currentMonth.length === 2 && currentYear.length === 4) {
      const monthNum = parseInt(currentMonth);
      const yearNum = parseInt(currentYear);
      if (monthNum >= 1 && monthNum <= 12 && yearNum >= MIN_YEAR && yearNum <= MAX_YEAR) {
        setViewYear(yearNum);
        setViewMonth(monthNum - 1); // Calendar uses 0-11 for months
      }
    }

    // ✅ NEW: AUTO-SELECT DATE IN CALENDAR WHEN VALID DATE IS TYPED
    if (isValid && currentDay.length === 2 && currentMonth.length === 2 && currentYear.length === 4) {
      const formatted = `${currentDay.padStart(2, "0")}-${currentMonth.padStart(2, "0")}-${currentYear}`;
      const parsed = parseDisplay(formatted);
      if (parsed) {
        // Update calendar to show selected date
        setViewYear(parsed.getFullYear());
        setViewMonth(parsed.getMonth());
        
        // Emit the change to parent component
        onChange?.({ target: { value: toISO(parsed) } });
        setTouched(true);
        if (required) setTypingError("");
      }
    }
  }; // ← FIXED: Added missing closing brace for handlePartChange

  const getYearGridStart = () => viewYear - (viewYear % 12);
  const yearStart = getYearGridStart();
  const years = Array.from({ length: 12 }, (_, i) => yearStart + i);

  const headerBg = darkMode ? "bg-[#2C2C2C]" : "bg-white";
  const headerBorder = darkMode ? "border-[#3C3C3C]" : "border-gray-200";
  const textCol = darkMode ? "text-[#E0E0E0]" : "text-gray-900";
  const subText = darkMode ? "text-gray-400" : "text-gray-500";

  const hasError = Boolean(error || typingError);
  const triggerClass = `block w-full px-3 pt-3 pb-2 text-sm rounded-none border text-left ${
    darkMode
      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
      : "bg-white border-gray-300 text-gray-900"
  } ${hasError ? "border-red-500" : ""} ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`;

  return (
    <div className="relative" ref={ref}>
      <div className={`${triggerClass} flex items-center gap-2`}>
        {label && (
          <div className={`absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 left-1 ${subText}`}>
            {label}
            {required && (
              <span className={`${darkMode ? 'text-red-400' : 'text-red-600'} ml-1`}>*</span>
            )}
          </div>
        )}
        
        <div className="flex items-center flex-1">
          {/* If month-year only AND showMonthName = true → show MONTH NAME */}
          {monthYearOnly && showMonthName ? (
            <div
              role="button"
              tabIndex={0}
              onClick={() => !disabled && setOpen(o => !o)}
              className={`w-full text-left px-1 py-1 text-sm font-medium ${
                !value || String(value).trim() === ""
                  ? subText
                  : darkMode
                    ? "text-[#E0E0E0]"
                    : "text-gray-900"
              }`}
            >
              {!value || String(value).trim() === ""
                ? monthYearEmptyLabel || formatMonthLabel(viewMonth, viewYear)
                : formatMonthLabel(viewMonth, viewYear)}
            </div>
          ) : (
          /*
            When monthYearOnly is true we hide the DD input entirely so
            the trigger shows "MM - YYYY". When false we show DD-MM-YYYY.
          */
         <>
          {!monthYearOnly && (
            <>
          {/* Day input */}
          <input
            ref={dayRef}
            type="text"
            inputMode="numeric"
            placeholder="DD"
            maxLength={2}
            onChange={(e) => handlePartChange('day', e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, monthRef, null)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            disabled={disabled}
            className={`w-7 text-center bg-transparent outline-none font-medium ${
              darkMode ? "placeholder-gray-400" : "placeholder-gray-500"
            }`}
          />
          <span className={`mx-0.5 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>-</span>
            </>
          )}
          {/* Month input */}
          <input
            ref={monthRef}
            type="text"
            inputMode="numeric"
            placeholder="MM"
            readOnly={monthYearOnly}
            maxLength={2}
            onChange={(e) => { if(!monthYearOnly) handlePartChange('month', e.target.value)}}
            onKeyDown={(e) => handleKeyDown(e, yearRef, dayRef)}
            onPaste={handlePaste}
            onFocus={(e) => { { if(!monthYearOnly)
              e.target.select();}
            }}
            disabled={disabled}
            className={`w-7 text-center bg-transparent outline-none font-medium ${
              darkMode ? "placeholder-gray-400" : "placeholder-gray-500"
            }`}
          />
          <span className={`mx-0.5 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>-</span>
          
          {/* Year input */}
          <input
            ref={yearRef}
            type="text"
            inputMode="numeric"
            placeholder="YYYY"
            maxLength={4}
            readOnly={monthYearOnly}
            onChange={(e) =>{ if(!monthYearOnly) handlePartChange('year', e.target.value)}}
            onKeyDown={(e) => handleKeyDown(e, null, monthRef)}
            onPaste={handlePaste}
            onFocus={(e) => {
              e.target.select();
            }}
            onBlur={() => { if(!monthYearOnly){
              // Validate year when user leaves the field
              const currentYear = yearRef.current?.value || "";
              const currentDay = dayRef.current?.value || "";
              const currentMonth = monthRef.current?.value || "";
              
              if (currentYear && currentYear.length > 0 && currentYear.length !== 4) {
                setTypingError("Year must be 4 digits");
              } else {
                validateDatePart(currentDay, currentMonth, currentYear);
              }
            }
            }}
            disabled={disabled}
            className={`w-11 text-center bg-transparent outline-none font-medium ${
              darkMode ? "placeholder-gray-400" : "placeholder-gray-500"
            }`}
          />
          </>
          )}
        </div>

        <button
          type="button"
          className={`shrink-0 p-1 rounded ${darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
          onClick={() => !disabled && setOpen((o) => !o)}
          aria-label="Toggle calendar"
        >
          <Calendar size={16} />
        </button>
      </div>

      {/* Calendar UI remains exactly the same */}
      {open && !disabled && (
        <div
          className={`absolute z-50 mt-2 w-80 max-w-[min(20rem,calc(100vw-1.5rem))] rounded shadow-xl border ${headerBorder} ${headerBg} ${
            dropdownAlign === "auto"
              ? dropdownPosition === "right"
                ? "right-0"
                : "left-0"
              : dropdownAlign === "right"
                ? "right-0"
                : "left-0"
          }`}
          role="dialog"
          aria-label={`${label || "date"} calendar`}
        >
          {/* Header with consistent button styles */}
          <div className={`flex items-center justify-between p-3 border-b ${headerBorder}`}>
            <div className="flex items-center space-x-2">
              {/* Previous button */}
              {(() => {
                // compute if prev is allowed
                let prevDisabled = false;
                if (view === "year") {
                  const prevStart = yearStart - 12;
                  const prevEnd = yearStart - 1;
                  const minYearAllowed = allowedMin.getFullYear();
                  prevDisabled = prevStart < minYearAllowed && prevEnd < minYearAllowed;
                } else {
                  const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1;
                  const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear;
                  const prevEnd = new Date(prevYear, prevMonth + 1, 0);
                  prevDisabled = prevEnd < allowedMin;
                }
                return (
                  <button
                    className={`px-2 py-1 rounded ${prevDisabled ? "opacity-40 cursor-not-allowed" : darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                    onClick={() => {
                      if (prevDisabled) return;
                      if (view === "year") {
                        setViewYear(yearStart - 12);
                      } else if (view === "month") {
                        const newMonth = viewMonth - 1;
                        if (newMonth < 0) {
                          setViewYear((y) => y - 1);
                          setViewMonth(11);
                        } else setViewMonth(newMonth);
                      } else {
                        const m = viewMonth - 1;
                        if (m < 0) {
                          setViewMonth(11);
                          setViewYear((y) => y - 1);
                        } else setViewMonth(m);
                      }
                    }}
                    aria-label="Previous"
                  >
                    ‹
                  </button>
                );
              })()}

              {/* Month button toggles month grid; keep same style */}
              <button
                className={`px-3 py-1 rounded font-medium ${textCol}`}
                onClick={() => setView((v) => (monthYearOnly ? "month" : v === "month" ? "day" : "month"))}
              >
                {months[viewMonth]}
              </button>

              {/* Year button always visible on top; clicking goes to year selection */}
              <button
                className={`px-3 py-1 rounded ${subText}`}
                onClick={() => setView("year")}
              >
                {viewYear}
              </button>

              {/* Next button */}
              {(() => {
                // compute if next is allowed
                let nextDisabled = false;
                if (view === "year") {
                  const nextStart = yearStart + 12;
                  const nextEnd = yearStart + 23;
                  const maxYearAllowed = allowedMax.getFullYear();
                  nextDisabled = nextStart > maxYearAllowed && nextEnd > maxYearAllowed;
                } else {
                  const nextMonth = viewMonth === 11 ? 0 : viewMonth + 1;
                  const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear;
                  const nextStart = new Date(nextYear, nextMonth, 1);
                  nextDisabled = nextStart > allowedMax;
                }
                return (
                  <button
                    className={`px-2 py-1 rounded ${nextDisabled ? "opacity-40 cursor-not-allowed" : darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                    onClick={() => {
                      if (nextDisabled) return;
                      if (view === "year") {
                        setViewYear(yearStart + 12);
                      } else if (view === "month") {
                        const newMonth = viewMonth + 1;
                        if (newMonth > 11) {
                          setViewYear((y) => y + 1);
                          setViewMonth(0);
                        } else setViewMonth(newMonth);
                      } else {
                        const m = viewMonth + 1;
                        if (m > 11) {
                          setViewMonth(0);
                          setViewYear((y) => y + 1);
                        } else setViewMonth(m);
                      }
                    }}
                    aria-label="Next"
                  >
                    ›
                  </button>
                );
              })()}
            </div>
          </div>

          {/* Calendar views remain exactly the same */}
          {view === "year" ? (
            <div className="p-3">
              <div className="grid grid-cols-3 gap-2">
                {years.map((y) => {
                  const minYearAllowed = allowedMin.getFullYear();
                  const maxYearAllowed = allowedMax.getFullYear();
                  const outOfRange = y < minYearAllowed || y > maxYearAllowed;
                  return (
                    <button
                      key={y}
                      disabled={outOfRange}
                      className={`py-2 rounded text-sm w-full ${
                        outOfRange
                          ? darkMode
                            ? "bg-[#2C2C2C] text-gray-600 cursor-not-allowed"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : y === viewYear
                          ? darkMode
                            ? "bg-[#BB86FC] text-black"
                            : "bg-blue-600 text-white"
                          : darkMode
                          ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]"
                          : "bg-white text-gray-900 border border-gray-200 hover:bg-gray-100"
                      }`}
                      onClick={() => {
                        if (outOfRange) return;
                        let mm = viewMonth;
                        const first = new Date(y, mm, 1);
                        const last = new Date(y, mm + 1, 0);
                        if (last < allowedMin) {
                          mm = allowedMin.getMonth();
                        } else if (first > allowedMax) {
                          mm = allowedMax.getMonth();
                        }
                        setViewYear(y);
                        setViewMonth(mm);
                        setView("month");
                      }}
                    >
                      {y}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : view === "month" ? (
            <div className="p-3">
              <div className="grid grid-cols-3 gap-2">
                {months.map((m, idx) => {
                  const monthStart = new Date(viewYear, idx, 1);
                  const monthEnd = new Date(viewYear, idx + 1, 0);
                  const outOfRange = monthEnd < allowedMin || monthStart > allowedMax;
                  return (
                    <button
                      key={m}
                      disabled={outOfRange}
                      className={`py-2 rounded text-sm w-full ${
                        outOfRange
                          ? darkMode
                            ? "bg-[#2C2C2C] text-gray-600 cursor-not-allowed"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : idx === viewMonth
                          ? darkMode
                            ? "bg-[#BB86FC] text-black"
                            : "bg-blue-600 text-white"
                          : darkMode
                          ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]"
                          : "bg-white text-gray-900 border border-gray-200 hover:bg-gray-100"
                      }`}
                      onClick={() => {
                        if (outOfRange) return;

                        if (monthYearOnly) {
                          const yearNum = viewYear;
                          const monthIndex = idx;
                          const first = `${yearNum}-${String(monthIndex + 1).padStart(2, "0")}-01`;
                          const lastDay = daysInMonth(yearNum, monthIndex);
                          const last = `${yearNum}-${String(monthIndex + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

                          // visual updates
                          if (monthRef.current) monthRef.current.value = String(monthIndex + 1).padStart(2, "0");
                          if (yearRef.current) yearRef.current.value = String(yearNum);
                          if (dayRef.current) dayRef.current.value = "";

                          setInputText(formatMonthLabel(monthIndex, yearNum));
                          onChange?.({ target: { value: `${first}/${last}` } });
                          setViewYear(yearNum);
                          setViewMonth(monthIndex);
                          setOpen(false);
                          setTouched(true);
                          if (required) setTypingError("");
                          return;
                        }

                        // normal behavior (day selection view)
                        setViewMonth(idx);
                        setView("day");
                      }}

                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="p-3">
              <div className={`grid grid-cols-7 text-center text-xs ${subText} mb-2`}>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {(() => {
                  const lead = firstDayIndex(viewYear, viewMonth);
                  const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1;
                  const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear;
                  const prevDays = daysInMonth(prevYear, prevMonth);
                  const cells = [];
                  for (let i = lead - 1; i >= 0; i--) {
                    const day = prevDays - i;
                    const date = new Date(prevYear, prevMonth, day);
                    const s = stripTime(date);
                    const outOfRange = isBeforeMin(s) || isAfterMax(s);
                    cells.push(
                      <button
                        key={`prev-${day}`}
                        disabled={outOfRange}
                        className={`h-8 rounded text-sm flex items-center justify-center ${darkMode ? "text-gray-500" : "text-gray-400"} ${outOfRange ? "cursor-not-allowed" : darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                        onClick={() => !outOfRange && selectDate(date)}
                      >
                        {String(day).padStart(2, "0")}
                      </button>
                    );
                  }
                  const thisDays = daysInMonth(viewYear, viewMonth);
                  for (let d = 1; d <= thisDays; d++) {
                    const date = new Date(viewYear, viewMonth, d);
                    const s = stripTime(date);
                    const disabledDay = isBeforeMin(s) || isAfterMax(s);
                    const isSelected = value && new Date(value).toDateString() === date.toDateString();
                    cells.push(
                      <button
                        key={`cur-${d}`}
                        disabled={disabledDay}
                        className={`h-8 rounded text-sm flex items-center justify-center ${
                          disabledDay
                            ? darkMode
                              ? "text-gray-500 cursor-not-allowed"
                              : "text-gray-400 cursor-not-allowed"
                            : isSelected
                              ? darkMode
                                ? "bg-[#BB86FC] text-black"
                                : "bg-blue-600 text-white"
                              : darkMode
                                ? "hover:bg-[#3C3C3C] text-[#E0E0E0]"
                                : "hover:bg-gray-100 text-gray-900"
                          }`}
                        onClick={() => selectDay(d)}
                      >
                        {String(d).padStart(2, "0")}
                      </button>
                    );
                  }
                  const rem = cells.length % 7;
                  const tailCount = rem === 0 ? 0 : 7 - rem;
                  const nextMonth = viewMonth === 11 ? 0 : viewMonth + 1;
                  const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear;
                  for (let d = 1; d <= tailCount; d++) {
                    const date = new Date(nextYear, nextMonth, d);
                    const s = stripTime(date);
                    const outOfRange = isBeforeMin(s) || isAfterMax(s);
                    cells.push(
                      <button
                        key={`next-${d}`}
                        disabled={outOfRange}
                        className={`h-7 rounded text-xs flex items-center justify-center ${darkMode ? "text-gray-500" : "text-gray-400"} ${outOfRange ? "cursor-not-allowed" : darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                        onClick={() => !outOfRange && selectDate(date)}
                      >
                        {String(d).padStart(2, "0")}
                      </button>
                    );
                  }
                  return cells;
                })()}
              </div>
              <div className={`mt-3 flex items-center justify-between border-t ${headerBorder} pt-2`}>
                {(() => {
                  const now = new Date();
                  const s = stripTime(now);
                  const todayDisabled = isBeforeMin(s) || isAfterMax(s);
                  return (
                    <button
                      disabled={todayDisabled}
                      className={`px-2 py-1 rounded text-sm ${
                        todayDisabled
                          ? 'opacity-40 cursor-not-allowed'
                          : darkMode
                          ? 'hover:bg-[#3C3C3C]'
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => {
                        if (todayDisabled) return;
                        if (monthYearOnly) {
                          const yearNum = today.getFullYear();
                          const monthIndex = today.getMonth();
                          const first = `${yearNum}-${String(monthIndex + 1).padStart(2, "0")}-01`;
                          const lastDay = daysInMonth(yearNum, monthIndex);
                          const last = `${yearNum}-${String(monthIndex + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

                          if (monthRef.current) monthRef.current.value = String(monthIndex + 1).padStart(2, "0");
                          if (yearRef.current) yearRef.current.value = String(yearNum);
                          if (dayRef.current) dayRef.current.value = "";

                          setInputText(formatMonthLabel(monthIndex, yearNum));
                          onChange?.({ target: { value: `${first}/${last}` } });
                          setViewYear(yearNum);
                          setViewMonth(monthIndex);
                          setOpen(false);
                          return;
                        }

                        // FIXED: Update input fields when clicking Today
                        const today = new Date();
                        if (dayRef.current) dayRef.current.value = String(today.getDate()).padStart(2, "0");
                        if (monthRef.current) monthRef.current.value = String(today.getMonth() + 1).padStart(2, "0");
                        if (yearRef.current) yearRef.current.value = String(today.getFullYear());
                        
                        setInputText(toDisplay(today));
                        onChange?.({ target: { value: toISO(today) } });
                        setViewYear(today.getFullYear());
                        setViewMonth(today.getMonth());
                        setOpen(false);
                        setTouched(true);
                        if (required) setTypingError("");
                      }}
                    >
                      Today
                    </button>
                  );
                })()}
                <button
                  className={`px-2 py-1 rounded text-sm ${darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                  onClick={() => {
                    // If required, prevent clearing and show error
                    if (required) {
                      setTouched(true);
                      setTypingError("This field is required");
                      return;
                    }
                    onChange?.({ target: { value: "" } });
                    setInputText("");
                    setOpen(false);
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {(error || typingError) && (
        <div className="mt-1 text-xs text-red-600">{error || typingError}</div>
      )}
    </div>
  );
} 